import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthError, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearAllCaches } from "@/lib/cache";
import { setCurrentAuthSession } from "@/lib/authSession";
import {
  getAppOpenRemainingMs,
  restartAppOpenAttempt,
  startAppOpenStage,
} from "@/lib/appOpenTelemetry";

export const AUTH_RESTORE_TIMEOUT_MS = 8000;

type AuthBootstrapError = "timeout" | "unavailable" | null;

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  bootstrapError: AuthBootstrapError;
  retryBootstrap: () => void;
  signUp: (email: string, password: string, userNumber?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

let linkedOneSignalUserId: string | null = null;
let pendingOneSignalUserId: string | null = null;
let cancelPendingOneSignal: (() => void) | null = null;
let oneSignalIsUnlinked = false;

function deferUntilAfterPaint(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  if (typeof window.requestAnimationFrame === "function") {
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(callback);
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }

  const timer = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timer);
}

function scheduleOneSignalLogin(authId: string): void {
  if (typeof window === "undefined" || !window.median?.onesignal) return;
  if (linkedOneSignalUserId === authId || pendingOneSignalUserId === authId) return;

  cancelPendingOneSignal?.();
  pendingOneSignalUserId = authId;
  oneSignalIsUnlinked = false;

  cancelPendingOneSignal = deferUntilAfterPaint(() => {
    cancelPendingOneSignal = null;
    void (async () => {
      try {
        await window.median?.onesignal?.login(authId);
        if (pendingOneSignalUserId === authId) linkedOneSignalUserId = authId;
      } catch {
        // Sem identificador nem erro nativo: console vai para Sentry Logs.
        console.warn("[OneSignal] não foi possível sincronizar o usuário");
      } finally {
        if (pendingOneSignalUserId === authId) pendingOneSignalUserId = null;
      }
    })();
  });
}

async function unlinkOneSignal(): Promise<void> {
  cancelPendingOneSignal?.();
  cancelPendingOneSignal = null;
  pendingOneSignalUserId = null;
  linkedOneSignalUserId = null;

  if (oneSignalIsUnlinked || typeof window === "undefined" || !window.median?.onesignal) return;
  oneSignalIsUnlinked = true;
  try {
    await window.median.onesignal.logout();
  } catch {
    console.warn("[OneSignal] não foi possível limpar o vínculo do usuário");
  }
}

class AuthRestoreTimeoutError extends Error {
  constructor() {
    super("auth restore deadline");
    this.name = "AuthRestoreTimeoutError";
  }
}

async function restoreSessionWithin(timeoutMs: number): Promise<Session | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new AuthRestoreTimeoutError()), timeoutMs);
    });
    const { data } = await Promise.race([supabase.auth.getSession(), timeout]);
    return data.session;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<AuthBootstrapError>(null);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const authEventVersion = useRef(0);
  const currentAuthId = useRef<string | null>(null);
  const mounted = useRef(true);

  const applySession = useCallback((next: Session | null) => {
    const nextAuthId = next?.user.id ?? null;
    if (currentAuthId.current && currentAuthId.current !== nextAuthId) {
      clearAllCaches();
    }
    currentAuthId.current = nextAuthId;
    setCurrentAuthSession(next);
    setSession(next);
    setUser(next?.user ?? null);
    setBootstrapError(null);
    setLoading(false);
    if (next?.user) scheduleOneSignalLogin(next.user.id);
  }, []);

  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, next) => {
      authEventVersion.current += 1;
      if (!mounted.current) return;
      applySession(next);
      if (event === "SIGNED_OUT") void unlinkOneSignal();
    });

    return () => {
      mounted.current = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;
    const eventVersionAtStart = authEventVersion.current;
    const stage = startAppOpenStage("bootstrap.auth");
    setLoading(true);
    setBootstrapError(null);

    void (async () => {
      try {
        const restored = await restoreSessionWithin(
          getAppOpenRemainingMs(AUTH_RESTORE_TIMEOUT_MS),
        );
        if (cancelled || authEventVersion.current !== eventVersionAtStart) {
          stage.end("cancelled");
          return;
        }
        applySession(restored);
        stage.end("ok");
      } catch (error) {
        if (cancelled || authEventVersion.current !== eventVersionAtStart) {
          stage.end("cancelled");
          return;
        }
        setLoading(false);
        setBootstrapError(error instanceof AuthRestoreTimeoutError ? "timeout" : "unavailable");
        stage.end(error instanceof AuthRestoreTimeoutError ? "timeout" : "error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applySession, restoreAttempt]);

  const retryBootstrap = useCallback(() => {
    restartAppOpenAttempt();
    setRestoreAttempt((value) => value + 1);
  }, []);

  const signUp = useCallback(async (email: string, password: string, userNumber?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { user_number: userNumber },
      },
    });
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    await unlinkOneSignal();
    clearAllCaches();
    setCurrentAuthSession(null);
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
      captchaToken,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    loading,
    bootstrapError,
    retryBootstrap,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  }), [
    user,
    session,
    loading,
    bootstrapError,
    retryBootstrap,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return context;
};

/** Somente para isolar testes do estado de módulo da bridge Median. */
export function __resetOneSignalForTests(): void {
  cancelPendingOneSignal?.();
  cancelPendingOneSignal = null;
  linkedOneSignalUserId = null;
  pendingOneSignalUserId = null;
  oneSignalIsUnlinked = false;
}
